import { useMemo, useState } from 'react';
import { CardWrapper, CardHeader, CardSection } from 'foundations-components/transitional/components/Card';
import { StatusTag } from 'foundations-components/transitional/components/StatusTag';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { VerticalMenuIcon } from 'foundations-assets/icons/components/VerticalMenuIcon';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { Link } from 'foundations-components/transitional/components/Link';
import { PaginationControls } from './PaginationControls';
import { INVOICE_STATUS, invoiceStatusToStatusTagUse, invoiceStatusToStatusTagMessage, invoiceFrequencyTypeToTypeMessage } from '../utils/typeAdapters';
import { useIsMobileView } from '../hooks/useWindowSize';
import { ROUTES } from './Routes';
import RouterButton from 'ui-addon-react-router-dom/RouterButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function InvoiceItem({
  invoice
}) {
  var _invoice$id, _invoice$downloadLink, _invoice$currencyCode, _invoiceFrequencyType;
  const [expanded, setExpanded] = useState(invoice.id === '1');
  const isMobileView = useIsMobileView();
  const IconButtonWrapper = props => /*#__PURE__*/_jsx(IconButton, Object.assign({}, props, {
    size: "extra-small",
    marginInline: "0",
    marginBlock: "0",
    children: /*#__PURE__*/_jsx(VerticalMenuIcon, {
      size: "100"
    })
  }));
  const statusTagUse = useMemo(() => {
    if (!invoice.status || !(invoice.status in invoiceStatusToStatusTagUse)) {
      return;
    }
    return invoiceStatusToStatusTagUse[invoice.status];
  }, [invoice.status]);
  const statusTagMessage = useMemo(() => {
    if (!invoice.status || !(invoice.status in invoiceStatusToStatusTagMessage)) {
      return;
    }
    return invoiceStatusToStatusTagMessage[invoice.status];
  }, [invoice.status]);
  const viewInvoiceUrl = useMemo(() => {
    if (!invoice.invoiceLink) {
      return '';
    }
    return `${ROUTES.invoice}?url=${encodeURIComponent(invoice.invoiceLink)}`;
  }, [invoice.invoiceLink]);
  return /*#__PURE__*/_jsxs(CardWrapper, {
    compact: true,
    expandable: true,
    expanded: expanded,
    onExpandedChange: newExpanded => setExpanded(newExpanded),
    marginBlockEnd: "300",
    children: [/*#__PURE__*/_jsx(CardHeader, {
      marginBlockStart: "600",
      titleSize: "small",
      title: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        rowGap: "100",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-100",
          children: (_invoice$id = invoice.id) !== null && _invoice$id !== void 0 ? _invoice$id : 'INV'
        }), !!statusTagUse && !!statusTagMessage && /*#__PURE__*/_jsx(StatusTag, {
          use: statusTagUse,
          hollow: false,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: statusTagMessage
          })
        })]
      }),
      toolbar: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        columnGap: "300",
        justifySelf: "end",
        children: [(invoice.status === INVOICE_STATUS.OPEN || invoice.status === INVOICE_STATUS.PAST_DUE) && /*#__PURE__*/_jsx(RouterButton, {
          use: "primary",
          paddingInline: "300",
          paddingBlock: "100",
          to: viewInvoiceUrl,
          disabled: !invoice.invoiceLink,
          children: /*#__PURE__*/_jsx(Text, {
            typography: "detail-100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "invoices.payNow"
            })
          })
        }), /*#__PURE__*/_jsx(Dropdown, {
          buttonUse: "tertiary-light",
          Button: IconButtonWrapper,
          menuWidth: 275,
          children: /*#__PURE__*/_jsxs(List, {
            children: [/*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(RouterButton, {
                to: viewInvoiceUrl,
                disabled: !invoice.invoiceLink,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "invoices.dropdown.view"
                })
              })
            }), /*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(Link, {
                href: (_invoice$downloadLink = invoice.downloadLink) !== null && _invoice$downloadLink !== void 0 ? _invoice$downloadLink : undefined,
                disabled: !invoice.downloadLink,
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "invoices.dropdown.download"
                })
              })
            })]
          })
        })]
      })
    }), /*#__PURE__*/_jsx(CardSection, {
      children: /*#__PURE__*/_jsxs(Flex, {
        gap: "800",
        flexDirection: isMobileView ? 'column' : 'row',
        children: [/*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "body-125",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "invoices.details"
            })
          }), /*#__PURE__*/_jsxs(List, {
            children: [/*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                message: "invoices.amountDue",
                options: {
                  amount: typeof invoice.amountDue === 'number' ? /*#__PURE__*/_jsx(FormattedIntlCurrency, {
                    value: invoice.amountDue,
                    currencyCode: (_invoice$currencyCode = invoice.currencyCode) !== null && _invoice$currencyCode !== void 0 ? _invoice$currencyCode : 'USD'
                  }) : /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "common.unknown"
                  })
                }
              })
            }), /*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                message: "invoices.invoiceDate",
                options: {
                  date: typeof invoice.invoiceDate === 'number' ? /*#__PURE__*/_jsx(FormattedDateTime, {
                    value: invoice.invoiceDate,
                    format: "LL"
                  }) : /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "common.unknown"
                  })
                }
              })
            }), /*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                message: "invoices.dueDate",
                options: {
                  date: typeof invoice.dueDate === 'number' ? /*#__PURE__*/_jsx(FormattedDateTime, {
                    value: invoice.dueDate,
                    format: "LL"
                  }) : /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "common.unknown"
                  })
                }
              })
            })]
          })]
        }), /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "body-125",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "invoices.type"
            })
          }), /*#__PURE__*/_jsx(List, {
            children: /*#__PURE__*/_jsx(ListItem, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: (_invoiceFrequencyType = invoiceFrequencyTypeToTypeMessage[invoice.invoiceFrequencyType]) !== null && _invoiceFrequencyType !== void 0 ? _invoiceFrequencyType : 'common.unknown'
              })
            })
          })]
        })]
      })
    })]
  });
}
export function InvoicesCard({
  invoices = [],
  pagination
}) {
  const isMobileView = useIsMobileView();
  return /*#__PURE__*/_jsxs(CardWrapper, {
    children: [/*#__PURE__*/_jsx(CardHeader, {
      padding: isMobileView ? '700' : undefined,
      title: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "invoices.title"
        }), pagination.loading && /*#__PURE__*/_jsx(LoadingSpinner, {
          margin: "0",
          size: "extra-small"
        })]
      })
    }), /*#__PURE__*/_jsx(CardSection, {
      padding: isMobileView ? '700' : undefined,
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        children: /*#__PURE__*/_jsx(PaginationControls, {
          isEmpty: invoices.length === 0,
          pagination: pagination,
          children: invoices.map(invoice => /*#__PURE__*/_jsx(InvoiceItem, {
            invoice: invoice
          }, invoice.id))
        })
      })
    })]
  });
}